/**
 * 완료 모임 화면의 후기 영역 — 카드 리스트 + 작성 + 댓글 + 좋아요 + 신고.
 * 사진 업로드는 후속 단계 (현재는 텍스트만).
 */
import React, { useEffect, useState, useCallback } from 'react';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import { useAlert } from 'contexts/AlertContext';
import { MeetingRepository } from 'repository/meetingRepository';
import { formatRelative } from 'common/utils/datetime';
import { IcChat, IcHeart, IcStarOutline } from 'components/common/icons/EmojiIcons';
import { Button, Section, SectionHeader, Sheet } from 'components/ds';
import styles from './MeetingPostsSection.module.css';

interface Post {
    postId: number;
    userNo: number;
    content: string;
    likeCount: number;
    commentCount: number;
    likedByMe?: boolean;
    isHidden?: boolean;
    authorNickname?: string;
    createdAt: string;
}

interface Comment {
    commentId: number;
    userNo: number;
    content: string;
    authorNickname?: string;
    createdAt: string;
}

interface Props {
    meetingId: string;
    currentUserNo: number;
    isCreator: boolean;
}

const REPORT_REASONS = [
    { code: 'INAPPROPRIATE', label: i18n.t('posts.reasonInappropriate') },
    { code: 'NSFW', label: i18n.t('posts.reasonNsfw') },
    { code: 'SPAM', label: i18n.t('posts.reasonSpam') },
    { code: 'PRIVACY', label: i18n.t('posts.reasonPrivacy') },
    { code: 'ETC', label: i18n.t('posts.reasonEtc') },
] as const;

export const MeetingPostsSection: React.FC<Props> = ({ meetingId, currentUserNo, isCreator }) => {
    const { t } = useTranslation();
    const { showAlert, showConfirm } = useAlert();
    const [posts, setPosts] = useState<Post[]>([]);
    const [loading, setLoading] = useState(false);
    const [composer, setComposer] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [expandedComments, setExpandedComments] = useState<Set<number>>(new Set());
    const [commentsByPost, setCommentsByPost] = useState<Record<number, Comment[]>>({});
    const [commentDrafts, setCommentDrafts] = useState<Record<number, string>>({});
    const [reportModal, setReportModal] = useState<{ targetType: 'POST' | 'COMMENT'; targetId: number } | null>(null);

    const fetchPosts = useCallback(async () => {
        setLoading(true);
        try {
            const res: any = await MeetingRepository.onGetMeetingPosts(meetingId);
            const raw = res?.data?.data ?? res?.data;
            setPosts(Array.isArray(raw) ? raw : []);
        } catch (e) {
            console.warn('후기 조회 실패:', e);
            setPosts([]);
        } finally {
            setLoading(false);
        }
    }, [meetingId]);

    useEffect(() => { fetchPosts(); }, [fetchPosts]);

    const handleCreatePost = async () => {
        const text = composer.trim();
        if (!text) return;
        setSubmitting(true);
        try {
            await MeetingRepository.onPostMeetingPost(meetingId, { content: text });
            setComposer('');
            await fetchPosts();
        } catch (e) {
            await showAlert({ message: t('posts.reviewFail') });
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeletePost = async (post: Post) => {
        const ok = await showConfirm({ title: t('posts.deleteReviewTitle'), message: t('posts.deleteReviewMsg'), confirmText: t('posts.delete') });
        if (!ok) return;
        try {
            await MeetingRepository.onDeleteMeetingPost(post.postId);
            await fetchPosts();
        } catch { await showAlert({ message: t('posts.deleteFail') }); }
    };

    const handleToggleLike = async (post: Post) => {
        // 옵티미스틱 업데이트
        setPosts(prev => prev.map(p => p.postId === post.postId
            ? { ...p, likedByMe: !p.likedByMe, likeCount: (p.likeCount || 0) + (p.likedByMe ? -1 : 1) }
            : p));
        try {
            await MeetingRepository.onPostToggleLike(post.postId);
        } catch {
            await fetchPosts(); // 실패 시 서버 상태로 복원
        }
    };

    const handleToggleComments = async (postId: number) => {
        const next = new Set(expandedComments);
        if (next.has(postId)) {
            next.delete(postId);
        } else {
            next.add(postId);
            if (!commentsByPost[postId]) {
                try {
                    const res: any = await MeetingRepository.onGetPostComments(postId);
                    const raw = res?.data?.data ?? res?.data;
                    setCommentsByPost(prev => ({ ...prev, [postId]: Array.isArray(raw) ? raw : [] }));
                } catch {
                    setCommentsByPost(prev => ({ ...prev, [postId]: [] }));
                }
            }
        }
        setExpandedComments(next);
    };

    const handleCreateComment = async (postId: number) => {
        const text = (commentDrafts[postId] || '').trim();
        if (!text) return;
        try {
            await MeetingRepository.onPostComment(postId, text);
            setCommentDrafts(prev => ({ ...prev, [postId]: '' }));
            // 댓글 목록 + 후기 list 둘 다 새로고침 (commentCount 갱신)
            const res: any = await MeetingRepository.onGetPostComments(postId);
            const raw = res?.data?.data ?? res?.data;
            setCommentsByPost(prev => ({ ...prev, [postId]: Array.isArray(raw) ? raw : [] }));
            setPosts(prev => prev.map(p => p.postId === postId ? { ...p, commentCount: (p.commentCount || 0) + 1 } : p));
        } catch {
            await showAlert({ message: t('posts.commentFail') });
        }
    };

    const handleDeleteComment = async (postId: number, commentId: number) => {
        const ok = await showConfirm({ title: t('posts.deleteCommentTitle'), message: t('posts.deleteCommentMsg'), confirmText: t('posts.delete') });
        if (!ok) return;
        try {
            await MeetingRepository.onDeleteComment(commentId);
            setCommentsByPost(prev => ({
                ...prev,
                [postId]: (prev[postId] || []).filter(c => c.commentId !== commentId),
            }));
            setPosts(prev => prev.map(p => p.postId === postId ? { ...p, commentCount: Math.max(0, (p.commentCount || 0) - 1) } : p));
        } catch { await showAlert({ message: t('posts.commentDeleteFail') }); }
    };

    const handleReport = async (reasonCode: typeof REPORT_REASONS[number]['code'], reasonText: string) => {
        if (!reportModal) return;
        try {
            await MeetingRepository.onPostReport({
                targetType: reportModal.targetType,
                targetId: reportModal.targetId,
                reasonCode,
                reasonText: reasonText || undefined,
            });
            setReportModal(null);
            await showAlert({ message: t('posts.reportDone') });
        } catch (e: any) {
            await showAlert({ message: e?.response?.data?.message || t('posts.reportFail') });
        }
    };

    return (
        <Section divided last>
            <SectionHeader title={t('posts.title', { count: posts.length })} />

            {/* 후기 쓰기 */}
            <div className={styles.composer}>
                <textarea
                    className={styles.textarea}
                    value={composer}
                    onChange={(e) => setComposer(e.target.value)}
                    placeholder={t('posts.reviewPlaceholder')}
                    rows={2}
                />
                <Button onClick={handleCreatePost} disabled={submitting || !composer.trim()}>
                    {submitting ? '...' : t('posts.submit')}
                </Button>
            </div>

            {loading && posts.length === 0 ? (
                <p className={styles.state}>{t('posts.loading')}</p>
            ) : posts.length === 0 ? (
                <p className={styles.state}>{t('posts.emptyPosts')}</p>
            ) : (
                <ul className={styles.list}>
                    {posts.map(p => {
                        const isMine = Number(p.userNo) === Number(currentUserNo);
                        const canDelete = isMine || isCreator;
                        const isExpanded = expandedComments.has(p.postId);
                        return (
                            <li key={p.postId} className={styles.post}>
                                <div className={styles.postHead}>
                                    <span className={styles.postAuthor}>
                                        {p.authorNickname || t('posts.userFallback', { no: p.userNo })}
                                        {p.isHidden && <span className={styles.postHidden}>{t('posts.hiddenByReport')}</span>}
                                    </span>
                                    <span className={styles.postTime}>{formatRelative(p.createdAt)}</span>
                                </div>
                                <p className={styles.postBody}>{p.content}</p>

                                <div className={styles.postActions}>
                                    <button
                                        type="button"
                                        className={p.likedByMe ? `${styles.count} ${styles.countOn}` : styles.count}
                                        onClick={() => handleToggleLike(p)}
                                        aria-pressed={!!p.likedByMe}
                                    >
                                        {p.likedByMe ? <IcHeart size={13} /> : <IcStarOutline size={13} />}
                                        {p.likeCount || 0}
                                    </button>
                                    <button
                                        type="button"
                                        className={styles.count}
                                        onClick={() => handleToggleComments(p.postId)}
                                        aria-expanded={isExpanded}
                                    >
                                        <IcChat size={13} />
                                        {p.commentCount || 0}
                                    </button>
                                    <span className={styles.spacer} />
                                    {canDelete && (
                                        <button type="button" className={styles.link} onClick={() => handleDeletePost(p)}>
                                            {t('posts.delete')}
                                        </button>
                                    )}
                                    {!isMine && (
                                        <button
                                            type="button"
                                            className={styles.link}
                                            onClick={() => setReportModal({ targetType: 'POST', targetId: p.postId })}
                                        >
                                            {t('posts.report')}
                                        </button>
                                    )}
                                </div>

                                {isExpanded && (
                                    <div className={styles.comments}>
                                        {(commentsByPost[p.postId] || []).map(c => {
                                            const cIsMine = Number(c.userNo) === Number(currentUserNo);
                                            const cCanDelete = cIsMine || isCreator;
                                            return (
                                                <div key={c.commentId} className={styles.comment}>
                                                    <span className={styles.commentBody}>
                                                        <span className={styles.commentAuthor}>
                                                            {c.authorNickname || t('posts.userFallback', { no: c.userNo })}
                                                        </span>
                                                        <span className={styles.commentText}>{c.content}</span>
                                                    </span>
                                                    <span className={styles.commentActions}>
                                                        {cCanDelete && (
                                                            <button
                                                                type="button"
                                                                className={styles.link}
                                                                onClick={() => handleDeleteComment(p.postId, c.commentId)}
                                                            >
                                                                {t('posts.delete')}
                                                            </button>
                                                        )}
                                                        {!cIsMine && (
                                                            <button
                                                                type="button"
                                                                className={styles.link}
                                                                onClick={() => setReportModal({ targetType: 'COMMENT', targetId: c.commentId })}
                                                            >
                                                                {t('posts.report')}
                                                            </button>
                                                        )}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                        <div className={styles.commentForm}>
                                            <input
                                                className={styles.commentInput}
                                                type="text"
                                                value={commentDrafts[p.postId] || ''}
                                                onChange={(e) => setCommentDrafts(prev => ({ ...prev, [p.postId]: e.target.value }))}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' && !e.shiftKey) {
                                                        e.preventDefault();
                                                        handleCreateComment(p.postId);
                                                    }
                                                }}
                                                placeholder={t('posts.commentPlaceholder')}
                                            />
                                            <Button size="sm" onClick={() => handleCreateComment(p.postId)}>
                                                {t('posts.submit')}
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}

            {reportModal && (
                <ReportModal onClose={() => setReportModal(null)} onSubmit={handleReport} />
            )}
        </Section>
    );
};

// ==================== Helpers ====================

interface ReportModalProps {
    onClose: () => void;
    onSubmit: (reasonCode: typeof REPORT_REASONS[number]['code'], reasonText: string) => void;
}

const ReportModal: React.FC<ReportModalProps> = ({ onClose, onSubmit }) => {
    const { t } = useTranslation();
    const [code, setCode] = useState<typeof REPORT_REASONS[number]['code']>('INAPPROPRIATE');
    const [text, setText] = useState('');

    return (
        <Sheet open onClose={onClose} title={t('posts.reportTitle')} closeLabel={t('posts.cancel')}>
            <p className={styles.reportLead}>{t('posts.reportSelectReason')}</p>
            <div className={styles.reasons}>
                {REPORT_REASONS.map(r => (
                    <label key={r.code} className={code === r.code ? `${styles.reason} ${styles.reasonOn}` : styles.reason}>
                        <input
                            type="radio"
                            name="reason"
                            value={r.code}
                            checked={code === r.code}
                            onChange={() => setCode(r.code)}
                        />
                        <span className={styles.reasonMark} aria-hidden />
                        {r.label}
                    </label>
                ))}
            </div>
            <textarea
                className={styles.textarea}
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={t('posts.reportDetailPlaceholder')}
                rows={3}
                maxLength={1000}
            />
            <p className={styles.reportNotice}>{t('posts.reportNotice')}</p>
            <div className={styles.reportActions}>
                <Button variant="secondary" block onClick={onClose}>{t('posts.cancel')}</Button>
                <Button variant="danger" block onClick={() => onSubmit(code, text)}>{t('posts.reportTitle')}</Button>
            </div>
        </Sheet>
    );
};

export default MeetingPostsSection;
